import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  useDisclosure,
  useToast,
} from '@elkaso-app/web-design';
import { useUploadFileApi } from 'apis/config/use-upload-file-api';
import { useGetDealCategoryTreeApi } from 'apis/exclusive-deals';
import { MasterProductFamily, useUpdateMasterProductFamilyApi } from 'apis/master-product-families';
import { findCategoryPath } from 'pages/exclusive-deals/components/editable-deals-grid/category-tree-helpers';
import type { CategoryRef } from 'pages/exclusive-deals/components/editable-deals-grid/editable-deal-row.types';
import { GridCategoryModal } from 'pages/exclusive-deals/components/editable-deals-grid/grid-category-modal';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FiChevronDown } from 'react-icons/fi';

export interface IEditFamilyModalCell {
  trigger: JSX.Element;
  family: MasterProductFamily;
}

interface FormData {
  universalName: string;
}

export const EditFamilyModal: React.FC<IEditFamilyModalCell> = ({ trigger, family }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [category, setCategory] = useState<CategoryRef | null>(null);
  const [imageUrl, setImageUrl] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const upload = useUploadFileApi({
    onSuccess: (data: any) => {
      const url = data?.url || data?.fileUrl || data?.path || '';
      if (url) setImageUrl(url);
    },
  });
  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const body = new FormData();
    body.append('file', file);
    upload.mutate({ body });
  };

  const { mutate: updateFamily, isLoading: isUpdating } = useUpdateMasterProductFamilyApi({
    onSuccess: (data) => {
      toast({ title: `Family "${data.universalName}" updated`, status: 'success' });
      onClose();
    },
    onError: (error: any) => {
      const status = error?.response?.status;
      const message =
        status === 409
          ? 'A family with this name already exists.'
          : error?.response?.data?.message || 'Failed to update family. Please try again.';
      toast({ title: message, status: 'error' });
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<FormData>({ mode: 'onChange' });

  useEffect(() => {
    if (isOpen && family) {
      reset({ universalName: family.universalName });
      setCategory({ id: family.catalogCategoryId, title: '' });
      setImageUrl(family.imageUrl ?? '');
    }
  }, [isOpen, family, reset]);

  const onSubmit = useCallback(
    (data: FormData) => {
      updateFamily({
        id: family.id,
        data: {
          universalName: data.universalName.trim(),
          catalogCategoryId: category?.id ?? family.catalogCategoryId,
          imageUrl: imageUrl || null,
        },
      });
    },
    [updateFamily, family, category, imageUrl]
  );

  const onCloseModal = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  // The stored family only carries the category ID — resolve the display name
  // (full "L1 › … › leaf" path) from the cached catalog tree instead of showing
  // "Category #<id>". A fresh pick from GridCategoryModal carries its own title.
  const { data: categoryTreeData } = useGetDealCategoryTreeApi({ enabled: isOpen });
  const categoryLabel = useMemo(() => {
    if (category?.title) return category.title;
    const id = category?.id ?? family.catalogCategoryId;
    const path = findCategoryPath(categoryTreeData?.data ?? [], id);
    return path.length ? path.map((node) => node.title).join(' › ') : `Category #${id}`;
  }, [category, categoryTreeData, family.catalogCategoryId]);

  return (
    <>
      {React.cloneElement(trigger, { onClick: onOpen })}

      <Modal
        isOpen={isOpen}
        onClose={onCloseModal}
        size={{ base: 'full', md: 'lg' }}
        scrollBehavior='inside'
        closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Family</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <FormControl isInvalid={!!errors.universalName} isRequired>
                <FormLabel>Universal Name</FormLabel>
                <Input
                  placeholder='e.g. Whole Chicken'
                  {...register('universalName', {
                    required: 'Universal name is required',
                    validate: (value) => value.trim().length > 0 || 'Universal name cannot be empty',
                  })}
                />
                <FormErrorMessage>{errors.universalName?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Category</FormLabel>
                <GridCategoryModal
                  value={category}
                  onSelect={setCategory}
                  trigger={
                    <Button
                      variant='outline'
                      w='100%'
                      justifyContent='space-between'
                      fontWeight='normal'
                      color={category ? 'gray.800' : 'gray.400'}
                      rightIcon={<FiChevronDown />}>
                      <Box as='span' isTruncated>
                        {categoryLabel}
                      </Box>
                    </Button>
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>Image</FormLabel>
                <Flex align='center' gap='3'>
                  {imageUrl ? (
                    <Image src={imageUrl} alt='family' boxSize='56px' objectFit='cover' rounded='md' />
                  ) : (
                    <Box boxSize='56px' bg='gray.100' rounded='md' />
                  )}
                  <input ref={fileRef} type='file' accept='image/*' hidden onChange={onFile} />
                  <Button
                    size='sm'
                    variant='outline'
                    isLoading={upload.isLoading}
                    onClick={() => fileRef.current?.click()}>
                    {imageUrl ? 'Replace image' : 'Upload image'}
                  </Button>
                  {imageUrl && (
                    <Button size='sm' variant='ghost' colorScheme='red' onClick={() => setImageUrl('')}>
                      Remove
                    </Button>
                  )}
                  {upload.isLoading && <Spinner size='sm' color='gray.400' />}
                </Flex>
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant='outline' mr={3} onClick={onCloseModal} isDisabled={isUpdating}>
              Cancel
            </Button>
            <Button
              colorScheme='red'
              onClick={handleSubmit(onSubmit)}
              isLoading={isUpdating}
              loadingText='Saving...'
              isDisabled={!isValid}>
              Save Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
